/**
 * useAccountHistory — polls two read-only backend endpoints for a given
 * account id:
 *   GET /api/v1/accounts/{account_id}/performance-history
 *   GET /api/v1/accounts/{account_id}/lockout-history
 *
 * Rules honoured:
 *   - Read-only. Never mutates anything, never touches broker credentials.
 *   - Empty arrays are surfaced explicitly as "no data" rather than zeroed.
 *   - HTTP 404 / non-2xx / network errors map to a clean UNAVAILABLE state
 *     instead of clobbering the last good render.
 *   - Treats `points: []` from a LIVE response as LIMITED, not UNAVAILABLE.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiV1Base } from '../lib/apiConfig';

export type AccountHistoryQuality = 'LIVE' | 'LIMITED' | 'UNAVAILABLE';

export interface PerformancePoint {
  timestamp: string;
  realized_pnl: number | null;
  unrealized_pnl: number | null;
  equity: number | null;
  source?: string | null;
  strategy_id?: string | null;
  notes?: string | null;
}

export interface PerformanceSummary {
  points_count: number;
  first_timestamp: string | null;
  last_timestamp: string | null;
  realized_pnl_total: number | null;
  unrealized_pnl_latest: number | null;
}

export interface PerformanceHistory {
  status: string;
  account_id: string;
  account_label: string | null;
  generated_at: string;
  points: PerformancePoint[];
  summary: PerformanceSummary;
  diagnostics_raw?: Record<string, unknown> | null;
}

export interface LockoutEvent {
  timestamp: string;
  strategy_id: string | null;
  lockout_type: string | null;
  reason: string | null;
  severity: string | null;
  duration_s: number | null;
  released_at: string | null;
  source?: string | null;
}

export interface LockoutSummary {
  events_count: number;
  active_lockouts: number;
  last_lockout_at: string | null;
  most_common_reason: string | null;
}

export interface LockoutHistory {
  status: string;
  account_id: string;
  account_label: string | null;
  generated_at: string;
  events: LockoutEvent[];
  summary: LockoutSummary;
  diagnostics_raw?: Record<string, unknown> | null;
}

export interface AccountHistoryBundle {
  performance: PerformanceHistory | null;
  lockouts: LockoutHistory | null;
  performanceQuality: AccountHistoryQuality;
  lockoutsQuality: AccountHistoryQuality;
  fetchedAt: string;
}

export interface UseAccountHistoryResult {
  bundle: AccountHistoryBundle | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const POLL_INTERVAL_MS = 60_000;

async function jget<T>(path: string): Promise<{ ok: boolean; data: T | null }> {
  try {
    const resp = await fetch(`${apiV1Base()}${path}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!resp.ok) return { ok: false, data: null };
    const body = await resp.json();
    if (body && typeof body === 'object' && 'ok' in body && body.ok === false) {
      return { ok: false, data: null };
    }
    const payload = body && typeof body === 'object' && 'data' in body ? body.data : body;
    return { ok: true, data: payload as T };
  } catch {
    return { ok: false, data: null };
  }
}

export function useAccountHistory(accountId: string | null | undefined): UseAccountHistoryResult {
  const [bundle, setBundle] = useState<AccountHistoryBundle | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(accountId));
  const [error, setError] = useState<string | null>(null);
  const firstLoadRef = useRef<boolean>(true);
  const idRef = useRef<string | null>(accountId ?? null);

  const load = useCallback(async () => {
    const id = idRef.current;
    if (!id) {
      setBundle(null);
      setLoading(false);
      return;
    }
    const encoded = encodeURIComponent(id);
    const [perfRes, lockRes] = await Promise.all([
      jget<PerformanceHistory>(`/accounts/${encoded}/performance-history`),
      jget<LockoutHistory>(`/accounts/${encoded}/lockout-history`),
    ]);

    const perfData = perfRes.ok ? perfRes.data : null;
    const lockData = lockRes.ok ? lockRes.data : null;

    const performanceQuality: AccountHistoryQuality = !perfRes.ok || !perfData
      ? 'UNAVAILABLE'
      : (perfData.points && perfData.points.length > 0 ? 'LIVE' : 'LIMITED');

    const lockoutsQuality: AccountHistoryQuality = !lockRes.ok || !lockData
      ? 'UNAVAILABLE'
      : (lockData.events && lockData.events.length > 0 ? 'LIVE' : 'LIMITED');

    setBundle({
      performance: perfData,
      lockouts: lockData,
      performanceQuality,
      lockoutsQuality,
      fetchedAt: new Date().toISOString(),
    });
    if (firstLoadRef.current) {
      setLoading(false);
      firstLoadRef.current = false;
    }
    if (!perfRes.ok && !lockRes.ok) {
      setError('Account history endpoints unavailable');
    } else {
      setError(null);
    }
  }, []);

  useEffect(() => {
    idRef.current = accountId ?? null;
    firstLoadRef.current = true;
    setLoading(Boolean(accountId));
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await load();
    };
    tick();
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      tick();
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [accountId, load]);

  return { bundle, loading, error, refetch: load };
}

export default useAccountHistory;
